import type { WasteStatsBucket } from "@/hooks/use-waste-stats";
import type { Lens } from "@/hooks/use-lens";
import type { WasteType } from "@/lib/waste-types";

/**
 * Where each waste type lands in the "Where the money goes" split. Three buckets
 * (POS2-947, expanding the earlier two-way split): avoidable = overproduction,
 * spoilage; unavoidable = preparation, post_consumer; unknown = unassigned — the
 * genuinely-blind residue the scorer could type as neither (obstructed, empty, or
 * unreadable capture). `packaging` is **excluded** from every bucket and the
 * food-waste totals (POS2-861). This supersedes POS2-938, which folded `unassigned`
 * into unavoidable — Mark (2026-07-20) split it back out into its own Unknown bar so
 * blind waste is never counted as "unavoidable". Exhaustive over `WasteType`: a new
 * type is a compile error here, never silently dropped.
 */
const WASTE_TYPE_GROUP: Record<WasteType, "avoidable" | "unavoidable" | "unknown" | "excluded"> = {
  overproduction: "avoidable",
  spoilage: "avoidable",
  preparation: "unavoidable",
  post_consumer: "unavoidable",
  unassigned: "unknown",
  packaging: "excluded",
};

type Amount = { cost: number; weight: number };

export type MoneySplit = {
  avoidable: Amount;
  unavoidable: Amount;
  unknown: Amount;
  /**
   * avoidable ÷ (avoidable + unavoidable) in the active lens, 0–100; null when both
   * are zero. Unknown is deliberately excluded — blind waste is neither recoverable
   * nor an accepted cost, so it can't move the recoverable headline.
   */
  recoverablePct: number | null;
};

export function moneySplit(buckets: WasteStatsBucket[], lens: Lens): MoneySplit {
  const avoidable: Amount = { cost: 0, weight: 0 };
  const unavoidable: Amount = { cost: 0, weight: 0 };
  const unknown: Amount = { cost: 0, weight: 0 };
  const byGroup = { avoidable, unavoidable, unknown };

  for (const bucket of buckets) {
    const group = WASTE_TYPE_GROUP[bucket.key as WasteType];
    // Fail loud on an unexpected type — never silently drop it (Code Style).
    if (group == null) throw new Error(`Unmapped waste type "${bucket.key}"`);
    if (group === "excluded") continue;
    byGroup[group].cost += bucket.total_cost;
    byGroup[group].weight += bucket.total_weight;
  }

  const a = lens === "weight" ? avoidable.weight : avoidable.cost;
  const u = lens === "weight" ? unavoidable.weight : unavoidable.cost;
  const recoverableTotal = a + u;
  return {
    avoidable,
    unavoidable,
    unknown,
    recoverablePct: recoverableTotal > 0 ? (a / recoverableTotal) * 100 : null,
  };
}
